/**
 * Cognito utilities
 * Uses direct HTTP calls to Cognito API (no AWS SDK required)
 */

/**
 * Storage keys for auth tokens
 */
export const AUTH_STORAGE_KEYS = {
  JWT: "JWT",
  JWT_REFRESH: "JWTrefresh",
  COGNITO_ID_TOKEN: "cognito_id_token",
  COGNITO_REFRESH_TOKEN: "cognito_refresh_token",
  COGNITO_EXPIRES_AT: "cognito_expires_at",
} as const;

/**
 * Clear all auth tokens from localStorage
 */
export function clearAuthTokens(): void {
  localStorage.removeItem(AUTH_STORAGE_KEYS.JWT);
  localStorage.removeItem(AUTH_STORAGE_KEYS.JWT_REFRESH);
  localStorage.removeItem(AUTH_STORAGE_KEYS.COGNITO_ID_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.COGNITO_REFRESH_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.COGNITO_EXPIRES_AT);
}

// Stale-session recovery: when an SSO callback fails (typically due to a stale
// .auth-dev.cyngn.com session cookie on Cognito's domain that this app can't
// clear from JS), we redirect through Cognito's /logout to wipe those cookies,
// then auto-retry the SSO flow. The counter caps the recovery loop so a real
// auth failure surfaces instead of looping forever.
// Uses sessionStorage so the counter is tab-scoped: if the user closes the tab
// mid-recovery, the stale counter won't follow them into a fresh session and
// auto-redirect them through SSO indefinitely.
const COGNITO_RETRY_KEY = "cognito_stale_session_retry";
export const MAX_COGNITO_RETRIES = 1;

export function getCognitoRetryCount(): number {
  return parseInt(sessionStorage.getItem(COGNITO_RETRY_KEY) || "0", 10);
}

export function incrementCognitoRetry(): void {
  sessionStorage.setItem(COGNITO_RETRY_KEY, String(getCognitoRetryCount() + 1));
}

export function resetCognitoRetry(): void {
  sessionStorage.removeItem(COGNITO_RETRY_KEY);
}

interface CognitoConfig {
  region: string;
  userPoolId: string;
  clientId: string;
}

interface CognitoRequest {
  ClientId: string;
  [key: string]: any;
}

export interface CognitoError extends Error {
  code?: string;
}

export interface NewPasswordChallenge {
  challengeName: "NEW_PASSWORD_REQUIRED";
  session: string;
  email: string;
}

export type CognitoSignInResult =
  | {
      type: "authenticated";
      tokens: {
        IdToken: string;
        AccessToken: string;
        RefreshToken: string;
        ExpiresIn: number;
      };
    }
  | {
      type: "new_password_required";
      challenge: NewPasswordChallenge;
    };

/**
 * Type guard for CognitoError
 * Excludes AxiosError which also has a 'code' property
 */
export function isCognitoError(error: unknown): error is CognitoError {
  return (
    error instanceof Error &&
    "code" in error &&
    !("isAxiosError" in error) &&
    !("response" in error)
  );
}

/**
 * Get Cognito configuration from environment variables
 */
function getCognitoConfig(): CognitoConfig {
  const region = window._env_.COGNITO_REGION;
  const userPoolId = window._env_.COGNITO_USER_POOL_ID;
  const clientId = window._env_.COGNITO_CLIENT_ID;

  if (!region || !userPoolId || !clientId) {
    console.error(
      "[Cognito] Config missing: region=%s, userPoolId=%s, clientId=%s",
      !!region,
      !!userPoolId,
      !!clientId
    );
    throw new Error(
      "Cognito configuration missing. Ensure COGNITO_REGION, COGNITO_USER_POOL_ID, and COGNITO_CLIENT_ID are set."
    );
  }

  return { region, userPoolId, clientId };
}

/**
 * Make a request to Cognito Identity Provider API
 */
async function cognitoRequest(
  action: string,
  body: CognitoRequest
): Promise<any> {
  const config = getCognitoConfig();
  const url = `https://cognito-idp.${config.region}.amazonaws.com/`;

  console.info(`[Cognito] ${action} request to ${config.region}`);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(
      data.message || "Cognito request failed"
    ) as CognitoError;
    // Extract error code from __type field (e.g., "NotAuthorizedException" from "com.amazon.cognito#NotAuthorizedException")
    error.code = data.__type?.split("#")[1];
    console.warn(
      `[Cognito] ${action} failed: ${error.code} - ${error.message}`
    );
    throw error;
  }

  console.info(`[Cognito] ${action} success`);
  return data;
}

/**
 * Initiate forgot password flow - sends verification code to user's email
 * @param email User's email address
 * @throws {CognitoError} If request fails
 */
export async function cognitoForgotPassword(email: string): Promise<void> {
  const config = getCognitoConfig();

  await cognitoRequest("ForgotPassword", {
    ClientId: config.clientId,
    Username: email.toLowerCase(),
  });
}

/**
 * Confirm new password with verification code
 * @param email User's email address
 * @param code Verification code sent to user's email
 * @param newPassword New password (must meet Cognito policy requirements)
 * @throws {CognitoError} If request fails (invalid code, weak password, etc.)
 */
export async function cognitoConfirmPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  const config = getCognitoConfig();

  await cognitoRequest("ConfirmForgotPassword", {
    ClientId: config.clientId,
    Username: email.toLowerCase(),
    ConfirmationCode: code,
    Password: newPassword,
  });
}

/**
 * Resend verification code (calls ForgotPassword again)
 * @param email User's email address
 * @throws {CognitoError} If request fails
 */
export async function cognitoResendCode(email: string): Promise<void> {
  await cognitoForgotPassword(email);
}

/**
 * Sign in user with email and password
 * @param email User's email address
 * @param password User's password
 * @returns Authentication tokens or a challenge requiring a new password
 * @throws {CognitoError} If authentication fails
 */
export async function cognitoSignIn(
  email: string,
  password: string
): Promise<CognitoSignInResult> {
  const config = getCognitoConfig();
  const result = await cognitoRequest("InitiateAuth", {
    ClientId: config.clientId,
    AuthFlow: "USER_PASSWORD_AUTH",
    AuthParameters: {
      USERNAME: email.toLowerCase(),
      PASSWORD: password,
    },
  });

  if (result.ChallengeName === "NEW_PASSWORD_REQUIRED") {
    console.info("[Cognito] NEW_PASSWORD_REQUIRED challenge received");
    return {
      type: "new_password_required",
      challenge: {
        challengeName: "NEW_PASSWORD_REQUIRED",
        session: result.Session,
        email: email.toLowerCase(),
      },
    };
  }

  if (result.ChallengeName) {
    console.warn(`[Cognito] Unsupported challenge: ${result.ChallengeName}`);
    const error = new Error(
      `Authentication challenge required: ${result.ChallengeName}`
    ) as CognitoError;
    error.code = result.ChallengeName;
    throw error;
  }

  console.info("[Cognito] Sign-in successful, tokens received");
  return {
    type: "authenticated",
    tokens: result.AuthenticationResult,
  };
}

/**
 * Complete the NEW_PASSWORD_REQUIRED challenge by setting a new password
 * @param session Session token from the challenge response
 * @param email User's email address
 * @param newPassword The new password to set
 * @returns Cognito authentication tokens
 * @throws {CognitoError} If the challenge response fails
 */
export async function cognitoCompleteNewPassword(
  session: string,
  email: string,
  newPassword: string
): Promise<{
  IdToken: string;
  AccessToken: string;
  RefreshToken: string;
  ExpiresIn: number;
}> {
  const config = getCognitoConfig();

  const result = await cognitoRequest("RespondToAuthChallenge", {
    ClientId: config.clientId,
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    Session: session,
    ChallengeResponses: {
      USERNAME: email.toLowerCase(),
      NEW_PASSWORD: newPassword,
    },
  });

  if (result.ChallengeName) {
    console.warn(
      `[Cognito] Unexpected challenge after NEW_PASSWORD_REQUIRED: ${result.ChallengeName}`
    );
    const error = new Error(
      `Additional authentication challenge required: ${result.ChallengeName}`
    ) as CognitoError;
    error.code = result.ChallengeName;
    throw error;
  }

  if (!result.AuthenticationResult) {
    throw new Error("No authentication result received from Cognito");
  }

  console.info("[Cognito] NEW_PASSWORD_REQUIRED challenge completed");
  return result.AuthenticationResult;
}

/**
 * Map Cognito error codes to user-friendly messages
 * @param error Error object with code property
 * @returns User-friendly error message
 */
export function getCognitoErrorMessage(error: any): string {
  switch (error.code) {
    case "NotAuthorizedException":
      return "Incorrect email or password";
    case "UserNotFoundException":
      return "No account found with this email";
    case "UserNotConfirmedException":
      return "Please verify your email before signing in";
    case "PasswordResetRequiredException":
      return "Password reset required. Please use Forgot Password.";
    case "NEW_PASSWORD_REQUIRED":
      return "Please set a new password to continue.";
    default:
      return error.message || "An error occurred during sign in";
  }
}
